import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import React, { FC, memo } from 'react';
import Icon from '~ComponentsImproved/Icon';
import ArrowForwardIcon from '~Assets/icons/arrow_forward.svg';
import { useButton, useCurrency } from '~Hooks';
import useI18n from '~Translations';
import { ChequeWithTableCode } from './HistoryOrdersList';

const OrderRowContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 8px 16px;
  text-transform: none;
  margin-bottom: 8px;
  height: 48px;
  box-sizing: border-box;
  align-items: center;
  position: relative;
  overflow: hidden;

  &:active {
    background-color: var(--colorBlueGray900);
  }

  border: ${({ isSelected }) =>
    isSelected ? '1px solid var(--colorBlueGray700)' : '1px solid var(--colorBlueGray1100)'};
`;

const OrderRowDetails = styled.div`
  color: var(--colorWhite);
  display: flex;
  font-weight: 600;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: calc(100% - 30px);
`;

const OrderColor = styled.div<{ color: string }>`
  position: absolute;
  left: 0;
  height: 100%;
  top: 0;
  width: 4px;
  background-color: ${({ color }) => color};
`;

interface ChequeRowProps {
  cheque: ChequeWithTableCode;
  isSelected: boolean;
  onChequeClick: (chequeId: string) => void;
}

/**
 * Individual cheque row component. Selection is passed in as a prop (instead of
 * read from the URL here) so a selection change only re-renders the affected rows.
 */
const ChequeRow: FC<ChequeRowProps> = ({ cheque, isSelected, onChequeClick }) => {
  const currency = useCurrency();
  const { i18n } = useI18n();

  // Handle order click - update URL with orderId parameter
  const rowButton = useButton({
    onClick: () => onChequeClick(cheque.id),
  });

  return (
    <OrderRowContainer {...rowButton} isSelected={isSelected}>
      <OrderColor color="var(--colorBlueGray300)" />
      <OrderRowDetails>
        <span>{i18n('History.Table') + ' ' + cheque.tableCode}</span>
        <span>{DateTime.fromISO(cheque.createdAt!).toFormat('HH:mm dd/MM')}</span>
        <span>{currency.format(cheque.paidAmount)}</span>
      </OrderRowDetails>
      {isSelected && <Icon src={ArrowForwardIcon} color="var(--colorWhite)" height="24px" />}
    </OrderRowContainer>
  );
};

export default memo(ChequeRow);
